import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, MessageCircle, AlertCircle, HelpCircle, ChevronDown, ChevronRight, FileText, Send, Paperclip, X } from 'lucide-react';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
interface SupportCenterProps {
  onBack: () => void;
}
export function SupportCenter({
  onBack
}: SupportCenterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubject, setContactSubject] = useState('General Inquiry');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const faqs = [{
    category: 'Getting Started',
    items: [{
      q: 'How do I create a listing?',
      a: 'To create a listing, go to your profile and tap "My Inventory", then click the plus icon. Follow the steps to add photos, description, and pricing.'
    }, {
      q: 'Is ID verification required?',
      a: 'Yes, for the safety of our community, all users must verify their identity before renting or listing items.'
    }]
  }, {
    category: 'Bookings & Rentals',
    items: [{
      q: 'How do I cancel a booking?',
      a: 'Go to "Bookings", select the trip, and tap "Cancel Booking". Cancellation fees may apply depending on the timing.'
    }, {
      q: 'What if an item is damaged?',
      a: 'Please document the damage immediately with photos and report it through the "Report an Issue" button on the booking details page.'
    }]
  }, {
    category: 'Payments',
    items: [{
      q: 'When do I get paid?',
      a: 'Payouts are processed 24 hours after the rental period successfully ends. It may take 3-5 business days to appear in your account.'
    }, {
      q: 'What payment methods are accepted?',
      a: 'We accept all major credit cards, debit cards, and Apple Pay.'
    }]
  }];
  const recentTickets = [{
    id: '#4829',
    subject: 'Refund Request',
    status: 'Open',
    date: 'Today'
  }, {
    id: '#3921',
    subject: 'ID Verification Issue',
    status: 'Resolved',
    date: '2 days ago'
  }];
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };
  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setShowContactModal(false);
    }, 1500);
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1573">
      {/* Header */}
      <div className="bg-white border-b border-sand sticky top-0 z-10" data-id="element-1574">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3" data-id="element-1575">
          <div className="flex items-center gap-3 mb-4" data-id="element-1576">
            <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-sand/20 transition-colors" data-id="element-1577">
              <ArrowLeft size={24} className="text-charcoal" data-id="element-1578" />
            </button>
            <h1 className="text-xl font-bold text-charcoal" data-id="element-1579">Support Center</h1>
          </div>

          {/* Search */}
          <div className="relative" data-id="element-1580">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-moss-gray" size={20} data-id="element-1581" />
            <input type="text" placeholder="How can we help?" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-cream rounded-ios text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green" data-id="element-1582" />
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-8" data-id="element-1583">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1584">
          <button onClick={() => {
          setContactSubject('General Inquiry');
          setShowContactModal(true);
        }} className="bg-white p-4 rounded-ios-lg shadow-ios flex flex-col items-center gap-2 hover:shadow-md transition-shadow" data-id="element-1585">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-1586">
              <MessageCircle size={20} data-id="element-1587" />
            </div>
            <span className="font-medium text-charcoal text-sm" data-id="element-1588">
              Contact Support
            </span>
          </button>
          <button onClick={() => {
          setContactSubject('Report Issue');
          setShowContactModal(true);
        }} className="bg-white p-4 rounded-ios-lg shadow-ios flex flex-col items-center gap-2 hover:shadow-md transition-shadow" data-id="element-1589">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600" data-id="element-1590">
              <AlertCircle size={20} data-id="element-1591" />
            </div>
            <span className="font-medium text-charcoal text-sm" data-id="element-1592">
              Report Issue
            </span>
          </button>
        </div>

        {/* Recent Tickets */}
        <div data-id="element-1593">
          <h2 className="text-lg font-bold text-charcoal mb-3" data-id="element-1594">
            Recent Tickets
          </h2>
          <div className="space-y-3" data-id="element-1595">
            {recentTickets.map(ticket => <div key={ticket.id} className="bg-white p-4 rounded-ios-lg shadow-ios flex items-center justify-between" data-id="element-1596">
                <div data-id="element-1597">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1598">
                    <span className="text-xs font-bold text-moss-gray" data-id="element-1599">
                      {ticket.id}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ticket.status === 'Open' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`} data-id="element-1600">
                      {ticket.status}
                    </span>
                  </div>
                  <div className="font-medium text-charcoal" data-id="element-1601">
                    {ticket.subject}
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-1602">
                  <span className="text-xs text-moss-gray" data-id="element-1603">{ticket.date}</span>
                  <ChevronRight size={16} className="text-moss-gray" data-id="element-1604" />
                </div>
              </div>)}
          </div>
        </div>

        {/* FAQs */}
        <div data-id="element-1605">
          <h2 className="text-lg font-bold text-charcoal mb-3" data-id="element-1606">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4" data-id="element-1607">
            {faqs.map((category, idx) => <div key={idx} className="bg-white rounded-ios-lg shadow-ios overflow-hidden" data-id="element-1608">
                <div className="bg-sand/20 px-4 py-2 font-semibold text-sm text-charcoal" data-id="element-1609">
                  {category.category}
                </div>
                {category.items.map((item, itemIdx) => {
              const isOpen = activeAccordion === `${idx}-${itemIdx}`;
              return <div key={itemIdx} className="border-b border-sand last:border-0" data-id="element-1610">
                      <button onClick={() => toggleAccordion(`${idx}-${itemIdx}`)} className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-sand/5 transition-colors" data-id="element-1611">
                        <span className="font-medium text-charcoal text-sm pr-4" data-id="element-1612">
                          {item.q}
                        </span>
                        <ChevronDown size={16} className={`text-moss-gray transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1613" />
                      </button>
                      <AnimatePresence data-id="element-1614">
                        {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden" data-id="element-1615">
                            <div className="px-4 pb-4 text-sm text-moss-gray leading-relaxed" data-id="element-1616">
                              {item.a}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            })}
              </div>)}
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <Modal isOpen={showContactModal} onClose={() => setShowContactModal(false)} title="Contact Support" data-id="element-1617">
        <form onSubmit={handleContactSubmit} className="space-y-4" data-id="element-1618">
          <div data-id="element-1619">
            <label className="block text-sm font-medium text-charcoal mb-1" data-id="element-1620">
              Topic
            </label>
            <select value={contactSubject} onChange={e => setContactSubject(e.target.value)} className="w-full px-4 py-3 bg-cream rounded-ios text-charcoal border-none focus:ring-2 focus:ring-dark-green" data-id="element-1621">
              <option data-id="element-1622">General Inquiry</option>
              <option data-id="element-1623">Booking Issue</option>
              <option data-id="element-1624">Payment Problem</option>
              <option data-id="element-1625">Report User/Listing</option>
              <option data-id="element-1626">Technical Support</option>
            </select>
          </div>

          <div data-id="element-1627">
            <label className="block text-sm font-medium text-charcoal mb-1" data-id="element-1628">
              Message
            </label>
            <textarea rows={4} placeholder="Describe your issue in detail..." className="w-full px-4 py-3 bg-cream rounded-ios text-charcoal placeholder-moss-gray border-none focus:ring-2 focus:ring-dark-green resize-none" required data-id="element-1629" />
          </div>

          <div className="flex items-center gap-2 text-dark-green text-sm font-medium cursor-pointer hover:opacity-80" data-id="element-1630">
            <Paperclip size={16} data-id="element-1631" />
            <span data-id="element-1632">Attach Screenshot or File</span>
          </div>

          <div className="pt-2" data-id="element-1633">
            <Button type="submit" variant="primary" fullWidth loading={isSubmitting} data-id="element-1634">
              Send Message
            </Button>
          </div>
        </form>
      </Modal>
    </div>;
}